import { type UserMissionGroup } from "@rescuetablet/missions-client";
import { useMissionsUser } from "@rescuetablet/missions-client/react";
import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { log } from "./config";
import { useMissionsClient } from "./hooks";

type MissionGroups = {
  readonly groups: ReadonlyArray<UserMissionGroup>;
  /** Whether `groups` were confirmed by the Missions API (and not only taken from Firestore). */
  readonly verified: boolean;
};

const MissionGroupsContext = createContext<MissionGroups>({
  groups: [],
  verified: false,
});

/**
 * The group permissions of the Firestore user document can be stale (e.g. its offline cache
 * still has a `create` permission that was removed in the portal). The Missions API (`GET /user`)
 * is what checks a request, so its groups win — reloaded whenever the user document changes.
 */
export function MissionGroupsProvider({ children }: { children: ReactNode }) {
  const user = useMissionsUser();
  const client = useMissionsClient();
  const [apiGroups, setApiGroups] =
    useState<ReadonlyArray<UserMissionGroup>>();

  useEffect(() => {
    setApiGroups(undefined);
    if (!user) return;

    let current = true;
    client
      .getUser()
      .then((info) => {
        if (current) setApiGroups(info.groups);
      })
      .catch((error) => log.warn("Loading the Missions user failed", error));
    return () => {
      current = false;
    };
  }, [client, user]);

  const value = apiGroups
    ? { groups: apiGroups, verified: true }
    : { groups: user?.groups ?? [], verified: false };

  return (
    <MissionGroupsContext.Provider value={value}>
      {children}
    </MissionGroupsContext.Provider>
  );
}

export function useMissionGroups(): ReadonlyArray<UserMissionGroup> {
  return useContext(MissionGroupsContext).groups;
}

/**
 * Creating a mission needs a `create` permission in at least one group. Only offered once the
 * Missions API confirmed it — creating a mission needs the API anyway.
 */
export function useCanCreateMission(): boolean {
  const { groups, verified } = useContext(MissionGroupsContext);
  return verified && groups.some((g) => g.permissions.includes("create"));
}
